/**
 * FlyoutDebugPanel — Temporary debug panel for tuning flyout animation parameters.
 *
 * Usage: new FlyoutDebugPanel(document.body);
 * Toggle with Ctrl+Shift+F.
 *
 * Each flyout group is selectable via a dropdown. All associated numeric
 * values appear as labeled range sliders with live numeric readout.
 * Changes are applied instantly to the mutable config objects in AnimationConfig.ts.
 */

import {
  DiceResultAnims,
  CashWinAnims,
  DiceEquippedAnims,
} from '../../game/config/AnimationConfig';

type ConfigGroup = Record<string, number | string>;

/** Deep-clone numeric+string values from a config object */
function snapshotConfig(config: ConfigGroup): Record<string, number | string> {
  const snap: Record<string, number | string> = {};
  for (const key of Object.keys(config)) {
    snap[key] = config[key];
  }
  return snap;
}

interface FlyoutCategory {
  label: string;
  config: ConfigGroup;
  /** Per-key slider range overrides: [min, max, step] */
  ranges?: Record<string, [number, number, number]>;
  /** Snapshot of original values for reset */
  defaults?: Record<string, number | string>;
}

const CATEGORIES: FlyoutCategory[] & { _defaultsSnapshotted?: boolean } = [
  {
    label: 'Dice Result',
    config: DiceResultAnims as unknown as ConfigGroup,
    ranges: {
      scaleInDuration: [0, 2, 0.01],
      scaleInPeak: [0.5, 3, 0.05],
      scaleInStagger: [0, 0.5, 0.005],
      preFlightPause: [0, 1, 0.01],
      flyDuration: [0, 2, 0.01],
      flyStagger: [0, 0.5, 0.005],
      flyArcHeight: [0, 200, 1],
      flyerFadeStart: [0, 1, 0.01],
      trailCount: [0, 5, 1],
      trailDelay: [0, 0.2, 0.005],
      landerDuration: [0, 2, 0.01],
      landerStagger: [0, 0.5, 0.005],
    },
  },
  {
    label: 'Cash Win',
    config: CashWinAnims as unknown as ConfigGroup,
    ranges: {
      scaleInDuration: [0, 2, 0.01],
      scaleInPeak: [0.5, 3, 0.05],
      noteStagger: [0, 0.5, 0.01],
      preFlightPause: [0, 1, 0.01],
      flyDuration: [0, 2, 0.01],
      flyArcHeight: [0, 200, 1],
      flyerFadeStart: [0, 1, 0.01],
      trailCount: [0, 5, 1],
      trailDelay: [0, 0.2, 0.005],
      noteSize: [10, 100, 1],
      maxNotes: [1, 15, 1],
      amountPopDuration: [0, 2, 0.01],
      amountLingerDuration: [0, 5, 0.1],
      amountFadeDuration: [0, 2, 0.01],
      betIconSize: [16, 100, 1],
      betIconGap: [0, 30, 1],
      betIconYOffset: [0, 200, 1],
      betIconInDuration: [0, 2, 0.01],
      betIconInStagger: [0, 0.5, 0.005],
      betIconOutDuration: [0, 2, 0.01],
      betIconOutStagger: [0, 0.5, 0.005],
    },
  },
  {
    label: 'Dice Equipped',
    config: DiceEquippedAnims as unknown as ConfigGroup,
    ranges: {
      popSize: [16, 120, 1],
      popGap: [0, 30, 1],
      popInDuration: [0, 2, 0.01],
      popInBaseDelay: [0, 1, 0.01],
      popInStagger: [0, 0.5, 0.005],
      flyStart: [0, 3, 0.01],
      flyDuration: [0, 2, 0.01],
    },
  },
];

// Default slider range for keys not explicitly mapped
const DEFAULT_RANGE: [number, number, number] = [0, 2, 0.01];

export class FlyoutDebugPanel {
  private root: HTMLElement;
  private body: HTMLElement;
  private select: HTMLSelectElement;
  private visible = false;

  constructor(container: HTMLElement) {
    this.root = document.createElement('div');
    this.root.className = 'flyout-debug-panel';
    this.root.style.cssText = `
      position: fixed; top: 8px; right: 8px; width: 320px;
      max-height: 90vh; overflow-y: auto;
      background: rgba(8, 8, 10, 0.92); border: 1px solid rgba(255,255,255,0.12);
      border-radius: 10px; padding: 10px 12px; z-index: 99999;
      font-family: 'Rajdhani', sans-serif; font-size: 13px; color: #ddd;
      display: none; backdrop-filter: blur(6px);
      scrollbar-width: thin; scrollbar-color: #444 transparent;
    `;

    // Header
    const header = document.createElement('div');
    header.style.cssText = 'display:flex; align-items:center; gap:8px; margin-bottom:8px;';
    const title = document.createElement('span');
    title.textContent = 'Flyout Tuner';
    title.style.cssText = 'font-weight:700; font-size:15px; color:#FFD700; flex:1;';
    header.appendChild(title);

    this.select = document.createElement('select');
    this.select.style.cssText = `
      background: #222; color: #fff; border: 1px solid #555;
      border-radius: 4px; padding: 3px 6px; font-size: 12px;
      font-family: inherit; cursor: pointer;
    `;
    for (const cat of CATEGORIES) {
      const opt = document.createElement('option');
      opt.value = cat.label;
      opt.textContent = cat.label;
      this.select.appendChild(opt);
    }
    this.select.addEventListener('change', () => this.renderCategory());
    header.appendChild(this.select);

    const resetBtn = document.createElement('button');
    resetBtn.textContent = 'Reset';
    resetBtn.style.cssText = `
      background: #333; color: #FF3366; border: 1px solid #FF3366;
      border-radius: 4px; padding: 3px 8px; font-size: 11px;
      font-family: inherit; font-weight: 700; cursor: pointer;
    `;
    resetBtn.addEventListener('click', () => this.resetCategory());
    header.appendChild(resetBtn);

    this.root.appendChild(header);

    this.body = document.createElement('div');
    this.root.appendChild(this.body);

    container.appendChild(this.root);

    // Snapshot defaults on first construction
    if (!CATEGORIES._defaultsSnapshotted) {
      for (const cat of CATEGORIES) {
        cat.defaults = snapshotConfig(cat.config);
      }
      CATEGORIES._defaultsSnapshotted = true;
    }

    this.renderCategory();

    // Toggle with Ctrl+Shift+F
    window.addEventListener('keydown', (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'F') {
        e.preventDefault();
        this.toggle();
      }
    });
  }

  private toggle(): void {
    this.visible = !this.visible;
    this.root.style.display = this.visible ? 'block' : 'none';
    if (this.visible) this.renderCategory();
  }

  private resetCategory(): void {
    const cat = this.getCategory();
    if (!cat.defaults) return;
    for (const key of Object.keys(cat.defaults)) {
      (cat.config as Record<string, number | string>)[key] = cat.defaults[key];
    }
    this.renderCategory();
  }

  private getCategory(): FlyoutCategory {
    return CATEGORIES.find((c) => c.label === this.select.value) ?? CATEGORIES[0];
  }

  private renderCategory(): void {
    this.body.innerHTML = '';
    const cat = this.getCategory();
    const keys = Object.keys(cat.config);

    for (const key of keys) {
      const val = cat.config[key];
      // Only show numeric values as sliders; skip easing strings
      if (typeof val !== 'number') continue;

      const [min, max, step] = cat.ranges?.[key] ?? DEFAULT_RANGE;
      const row = document.createElement('div');
      row.style.cssText = 'display:flex; align-items:center; gap:6px; margin-bottom:5px;';

      const label = document.createElement('label');
      label.textContent = key;
      label.style.cssText = 'flex:1; font-size:11px; color:#aaa; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;';
      label.title = key;

      const slider = document.createElement('input');
      slider.type = 'range';
      slider.min = String(min);
      slider.max = String(max);
      slider.step = String(step);
      slider.value = String(val);
      slider.style.cssText = 'width:100px; accent-color:#FFD700; cursor:pointer;';

      const readout = document.createElement('span');
      readout.textContent = this.fmtNum(val, step);
      readout.style.cssText = 'width:42px; text-align:right; font-size:11px; color:#00FF88; font-variant-numeric: tabular-nums;';

      slider.addEventListener('input', () => {
        const v = parseFloat(slider.value);
        (cat.config as Record<string, number>)[key] = v;
        readout.textContent = this.fmtNum(v, step);
      });

      row.appendChild(label);
      row.appendChild(slider);
      row.appendChild(readout);
      this.body.appendChild(row);
    }
  }

  private fmtNum(v: number, step: number): string {
    const decimals = step < 0.01 ? 3 : step < 0.1 ? 2 : step < 1 ? 1 : 0;
    return v.toFixed(decimals);
  }

  dispose(): void {
    this.root.remove();
  }
}
